import { isNotEmpty, isNotNull, OrgAdminFragment, useOrgAdminsQuery } from '@alice-financial/alice-sdk'
import { Box, Button, List, ListItem, ListItemText, TextField, Typography } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { QuestionPageDescription } from '../../survey/surveyBlocks'
import { useOrg } from '../organization/useOrg'
import { useOrgId } from '../useOrgId'
import { AdminRoleDialog } from './AdminRoleDialog'

type RoleConfig = {
  title: string
  description?: string
}

const ROLE_CONFIGS: RoleConfig[] = [
  {
    title: 'Payroll Manager',
    description: 'The person who presses submit to run payroll each pay period.',
  },
  {
    title: 'Bookkeeper',
    description: 'The person who maintains accurate financial records using a system like QuickBooks.',
  },
  {
    title: 'Accountant/Finance Leader',
    description: 'The person who prepares and reviews your monthly financial statements.',
  },
  {
    title: 'Controller',
    description: 'The person who pays invoices and approves bank transfers.',
  },
  {
    title: 'Benefits Consultant',
    description: 'The person who supports your team and helps with benefits strategy.',
  },
  {
    title: 'Head of People/HR',
    description: 'The person who decides what payroll system your company uses.',
  },
  {
    title: 'General Manager(s)',
    description: 'The person who manages a specific location or team.',
  },
]

const adminSort = (a: OrgAdminFragment, b: OrgAdminFragment) =>
  a.lastName?.localeCompare(b.lastName || '') || a.firstName?.localeCompare(b.firstName || '') || 0

type AdminRoleFormProps = {
  role: RoleConfig
  admins: Array<OrgAdminFragment>
}

const AdminRoleForm = ({ role, admins }: AdminRoleFormProps) => {
  const assignedAdmins = admins?.filter((admin) => admin.role?.label?.includes(role.title))
  const hasAssignedAdmins = assignedAdmins.length > 0

  const [isDialogOpen, setIsDialogOpen] = React.useState(false)

  return (
    <>
      <ListItem divider>
        <Box ml={0.5}>
          <Button
            variant={hasAssignedAdmins ? 'outlined' : 'contained'}
            size="small"
            color="primary"
            onClick={() => setIsDialogOpen(true)}
          >
            {hasAssignedAdmins ? 'Change' : 'Add'}
          </Button>
        </Box>
        <ListItemText
          primary={
            <Typography variant="body2" fontWeight="bold">
              {role.title}
              {hasAssignedAdmins && ': '}
              {`${assignedAdmins.map((admin) => `${admin.firstName} ${admin.lastName}`).join(', ')}`}
            </Typography>
          }
          secondary={<Typography variant="caption">{role.description || 'Custom role'}</Typography>}
        />
      </ListItem>

      <AdminRoleDialog
        roleTitle={role.title}
        roleDescription={role.description}
        admins={admins}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  )
}

const NewRoleForm = ({ admins }: { admins: Array<OrgAdminFragment> }) => {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [roleTitle, setRoleTitle] = React.useState('')

  return (
    <>
      <ListItem divider>
        <Box ml={0.5}>
          <Button variant="contained" size="small" color="primary" onClick={() => setIsDialogOpen(true)}>
            Add
          </Button>
        </Box>
        <ListItemText
          primary={
            <TextField
              size="small"
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              helperText="Add another role"
            />
          }
        />
      </ListItem>

      <AdminRoleDialog
        roleTitle={roleTitle}
        roleDescription="Custom role"
        admins={admins}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  )
}

const getCustomRoleConfigs = (admins: Array<OrgAdminFragment>): Array<RoleConfig> => {
  const roleTitles = ROLE_CONFIGS.map((role) => role.title)
  const customTitles = admins
    .flatMap((admin) => admin.role?.label?.split(',').filter((title) => !roleTitles.includes(title)))
    .filter(isNotEmpty)
  const uniqueCustomTitles = Array.from(new Set(customTitles))
  return uniqueCustomTitles.map((title) => ({ title }))
}

export const AdminRoleInputs = () => {
  const org = useOrg()
  const { data: orgAdminsData } = useOrgAdminsQuery({ orgId: useOrgId() })
  const admins = orgAdminsData?.usersWithRole?.filter(isNotNull).sort(adminSort) || []

  const customRoleConfigs = getCustomRoleConfigs(admins)
  const roleConfigs = [...ROLE_CONFIGS, ...customRoleConfigs]

  return (
    <Box pb={4}>
      {org?.isOnboarded && (
        <Box maxWidth="sm">
          <QuestionPageDescription>
            We&apos;ll set up a few main roles so they have the right permissions.
          </QuestionPageDescription>
          <QuestionPageDescription>
            If one person covers multiple roles, no worries &ndash; just enter their details in each required
            section. You can always add or edit admins later.
          </QuestionPageDescription>
        </Box>
      )}

      <List disablePadding>
        {roleConfigs.map((role) => (
          <AdminRoleForm key={role.title} role={role} admins={admins} />
        ))}
        <NewRoleForm key={roleConfigs.length} admins={admins} />
      </List>
    </Box>
  )
}
